// Client Commission Section - Shows commission history for a specific client
// Embedded in ClientProfile.jsx between PlanHistoryCard and ServiceTab

import React, { useState, useEffect } from 'react';
import { DollarSign, FileText, ChevronDown, ChevronUp, Loader2, ExternalLink, TrendingUp, TrendingDown, ArrowDownRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ClientCommissionSection = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    fetchCommissions();
  }, [clientId]);

  const fetchCommissions = async () => {
    try {
      const res = await fetch(`${API_BASE}/commissions/client/${clientId}`, {
        credentials: 'include'
      });
      if (res.status === 404) {
        // Client not found or no commission data - just hide section
        setData({ entries: [], summary: {} });
        return;
      }
      const result = await res.json();
      if (result.ok) setData(result);
    } catch (err) {
      console.error('Error fetching client commissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6">
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) return null;

  const summary = data?.summary || {};
  const entries = data?.entries || [];
  const totalPaid = parseFloat(summary.total_paid || 0);
  const totalChargebacks = Math.abs(parseFloat(summary.total_chargebacks || 0));
  const netCommissions = totalPaid - totalChargebacks;

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-[#172A3A] flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white">
            <DollarSign size={18} />
          </span>
          Commissions
        </h3>
        {entries.length > 0 && (
          <div className="text-right">
            <div className={`text-2xl font-bold ${netCommissions >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(netCommissions)}
            </div>
            <div className="text-xs text-gray-500">Net earned</div>
          </div>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-4 text-gray-400 text-sm">
          No commission entries recorded for this client
        </div>
      ) : (
        <>
          {/* Summary Row */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-600">{formatCurrency(totalPaid)}</div>
              <div className="text-xs text-green-700">Total Paid</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-red-600">{formatCurrency(totalChargebacks)}</div>
              <div className="text-xs text-red-700">Chargebacks</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-600">{summary.entry_count || 0}</div>
              <div className="text-xs text-blue-700">Entries</div>
            </div>
          </div>

          {/* Date Range */}
          {summary.first_payment_date && (
            <div className="text-xs text-gray-500 mb-3">
              {new Date(summary.first_payment_date).toLocaleDateString()} — {new Date(summary.last_payment_date).toLocaleDateString()}
            </div>
          )}

          {/* Expand/Collapse */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium mb-2"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide details' : `Show ${entries.length} entries`}
          </button>

          {/* Entries List */}
          {expanded && (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {entries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {entry.carrier_name || 'Unknown Carrier'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        entry.entry_type === 'paid' ? 'bg-green-100 text-green-700' :
                        entry.entry_type === 'chargeback' ? 'bg-red-100 text-red-700' :
                        entry.entry_type === 'expected' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.entry_type === 'paid' ? 'Paid' :
                         entry.entry_type === 'chargeback' ? 'Chargeback' :
                         entry.entry_type === 'expected' ? 'Expected' :
                         entry.entry_type}
                      </span>
                      {entry.commission_type && (
                        <span className="text-xs text-gray-500">
                          {entry.commission_type === 'initial' ? 'Initial' : entry.commission_type === 'renewal' ? 'Renewal' : entry.commission_type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {entry.effective_date && (
                        <span>{new Date(entry.effective_date).toLocaleDateString()}</span>
                      )}
                      {entry.statement_filename && (
                        <span className="flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer">
                          <FileText className="w-3 h-3" />
                          {entry.statement_filename}
                        </span>
                      )}
                      {entry.batch_statement_date && (
                        <span>Statement: {new Date(entry.batch_statement_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <span className={`font-semibold text-sm whitespace-nowrap ml-3 ${
                    entry.entry_type === 'chargeback' ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {entry.entry_type === 'chargeback' ? '-' : ''}{formatCurrency(entry.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientCommissionSection;
